import React, { useState, useEffect } from 'react';
import { Chess } from 'chess.js';
import { generateHomeworkSet } from '../../utils/homeworkData';
import { PIECE_SETS, BOARD_THEMES, playSound } from '../Board/BoardConstants';
import confetti from 'canvas-confetti';
import toast from 'react-hot-toast'; 
import { doc, updateDoc, arrayUnion, setDoc, addDoc, collection, increment, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

const FUN_POPUPS = ["BRILLIANT! 🚀", "EPIC MOVE! 🔥", "MASTERMIND! 🧠", "PERFECT! ⭐", "CRUSHING IT! 💥", "GENIUS! 🎓"];
const fireConfetti = () => { confetti({ particleCount: 200, spread: 100, origin: { y: 0.5 }, zIndex: 9999 }); };
const parseFenToBoard = (fen) => {
    const board = {};
    const rows = (fen || '8/8/8/8/8/8/8/8 w - - 0 1').split(' ')[0].split('/');
    const cols = ['a','b','c','d','e','f','g','h'];
    rows.forEach((row, rIdx) => {
        let cIdx = 0;
        for(let char of row) {
            if(!isNaN(char)) cIdx += parseInt(char);
            else { board[`${cols[cIdx]}${8-rIdx}`] = { type: char.toLowerCase(), color: char === char.toUpperCase() ? 'w' : 'b' }; cIdx++; }
        }
    });
    return board;
};

export default function HomeworkPlayer({ assignment, myProfile, onBack }) {
    const boardTheme = myProfile?.boardTheme || 'green';
    const pieceTheme = myProfile?.pieceTheme || 'cburnett';
    const currentPieceSet = PIECE_SETS[pieceTheme] || PIECE_SETS.cburnett;
    const { light: lightSquareColor, dark: darkSquareColor } = BOARD_THEMES[boardTheme] || BOARD_THEMES.green;

    const [puzzles, setPuzzles] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [currentFen, setCurrentFen] = useState("");
    const [initialFen, setInitialFen] = useState(""); 
    const [playerTurn, setPlayerTurn] = useState("w"); 
    const [selectedSquare, setSelectedSquare] = useState(null);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [shakeBoard, setShakeBoard] = useState(false);
    const [popupText, setPopupText] = useState("PERFECT! ⭐");

    const [puzzleLog, setPuzzleLog] = useState([]);
    const [currentAttempts, setCurrentAttempts] = useState([]);
    const [puzzleStep, setPuzzleStep] = useState(0); // 🔥 NAYA STATE FOR COMBO
    const [isLoading, setIsLoading] = useState(true);

    const loadPuzzleState = (puzzleObj) => {
        try {
            const game = new Chess(puzzleObj.fen);
            if (puzzleObj.opponentMove) {
                const fromSq = puzzleObj.opponentMove.substring(0, 2);
                const toSq = puzzleObj.opponentMove.substring(2, 4);
                const promo = puzzleObj.opponentMove.length === 5 ? puzzleObj.opponentMove[4] : 'q';
                game.move({ from: fromSq, to: toSq, promotion: promo });
            }
            const startingFen = game.fen();
            setCurrentFen(startingFen);
            setInitialFen(startingFen); 
            setPlayerTurn(game.turn()); 
            setCurrentAttempts([]); 
            setPuzzleStep(0); 
        } catch (e) {
            console.error("Invalid Puzzle FEN", e);
        }
    };

    const saveProgress = async (index, finalAttempts, isDone = false, customPuzzleLog = null) => {
        if (assignment?.isReplay) return;
        if (!myProfile?.email || !assignment?.assignmentDocId || assignment.assignmentDocId === 'INDIVIDUAL') return;
        const safeEmail = myProfile.email.toLowerCase();
        const docId = `${assignment.assignmentDocId}_${safeEmail.replace(/[^a-zA-Z0-9]/g, '')}`;
        
        try {
            const currentPuzzleObj = puzzles[index];
            const currentLogEntry = {
                puzzleNumber: index + 1,
                theme: currentPuzzleObj?.theme || "Tactics",
                startingFen: initialFen,
                attempts: finalAttempts
            };
            
            const activePuzzleLog = customPuzzleLog || puzzleLog;
            const cleanLogs = [...activePuzzleLog.filter(l => l.puzzleNumber !== index + 1), currentLogEntry]
                .sort((a, b) => a.puzzleNumber - b.puzzleNumber)
                .map(log => ({
                    puzzleNumber: log.puzzleNumber || 0,
                    theme: log.theme || "Tactics",
                    startingFen: log.startingFen || "",
                    attempts: log.attempts.map(att => ({
                        san: att.san || "", from: att.from || "", to: att.to || "", isCorrect: !!att.isCorrect
                    }))
                }));

            await setDoc(doc(db, "homework_submissions", docId), {
                homeworkId: assignment.assignmentDocId,
                levelId: assignment.levelId || '',
                homeworkTitle: assignment.title || 'Chess Mission',
                batchId: assignment.batchId || 'INDIVIDUAL',
                studentEmail: safeEmail,
                studentName: myProfile.name || safeEmail.split('@')[0] || "Student",
                submittedAt: new Date().toISOString(),
                currentIndex: index,
                totalPuzzles: puzzles.length,
                status: isDone ? 'completed' : 'in_progress',
                logs: cleanLogs
            }, { merge: true });
        } catch (e) {
            console.error("Error saving homework progress", e);
        }
    };

    useEffect(() => {
        const fetchAndLoadPuzzles = async () => {
            setIsLoading(true);
            const data = await generateHomeworkSet(assignment.levelId, assignment.chapterId);
            setPuzzles(data);
            
            if (!assignment.isReplay && myProfile?.email && assignment?.assignmentDocId && assignment.assignmentDocId !== 'INDIVIDUAL') {
                const safeEmail = myProfile.email.toLowerCase();
                const docId = `${assignment.assignmentDocId}_${safeEmail.replace(/[^a-zA-Z0-9]/g, '')}`;
                try {
                    const docSnap = await getDoc(doc(db, "homework_submissions", docId));
                    if (docSnap.exists()) {
                        const subData = docSnap.data();
                        if (subData.status === 'completed') {
                            setIsComplete(true);
                            setPuzzleLog(subData.logs || []);
                            setCurrentIndex(subData.currentIndex !== undefined ? subData.currentIndex : (data.length - 1));
                        } else {
                            const savedIndex = subData.currentIndex || 0;
                            setCurrentIndex(savedIndex);
                            setPuzzleLog(subData.logs || []);
                            if (data.length > savedIndex) {
                                loadPuzzleState(data[savedIndex]);
                            } else if (data.length > 0) {
                                loadPuzzleState(data[0]);
                            }
                        }
                        setIsLoading(false);
                        return;
                    }
                } catch (e) {
                    console.error("Error fetching saved progress", e);
                }
            }
            
            if (data.length > 0) loadPuzzleState(data[0]);
            setIsLoading(false);
        };
        fetchAndLoadPuzzles();
    }, [assignment, myProfile]);

    if (isLoading) return <div style={{color: 'var(--text-primary)', padding: '50px', textAlign: 'center', fontSize: '20px' }}>Analyzing Tactical Database... ⏳</div>;

    if (!currentFen || puzzles.length === 0) return (
        <div style={{background: 'var(--bg-primary)', minHeight: '80vh', padding: '50px', borderRadius: '20px', border: '2px solid rgba(163, 177, 198, 0.4)', textAlign: 'center', color: 'var(--text-primary)' }}>
            <h2 style={{fontSize: '32px', color: '#ef4444' }}>No Missions Found!</h2>
            <p style={{color: 'var(--text-secondary)', fontSize: '18px', marginBottom: '30px' }}>The academy is still forging missions for this level. Please check back later.</p>
            <button onClick={onBack} style={{padding: '15px 30px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '10px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)' }}>
                ⬅ Return to HQ Map
            </button>
        </div>
    );

    const currentPuzzle = puzzles[currentIndex];
    const displayBoard = parseFenToBoard(currentFen);

    const triggerError = (from, to) => {
        playSound('error');
        setShakeBoard(true);
        setTimeout(() => setShakeBoard(false), 500);
        setSelectedSquare(null);
        if(from && to) {
            setCurrentAttempts(prev => {
                const updated = [...prev, { from, to, isCorrect: false }];
                saveProgress(currentIndex, updated, false);
                return updated;
            });
        }
    };

    const executeMove = (from, to) => {
        if (isSuccess || isComplete) return;
        try {
            const game = new Chess(currentFen);
            const move = game.move({ from, to, promotion: 'q' });
            
            if (move) {
                // 🔥 THE COMBO FIX
                const expectedMove = currentPuzzle.solution[puzzleStep];
                const expectedFrom = expectedMove.substring(0, 2);
                const expectedTo = expectedMove.substring(2, 4);
                
                if (from === expectedFrom && to === expectedTo) {
                    setCurrentAttempts(prev => {
                        const updated = [...prev, { san: move.san, from, to, isCorrect: true }];
                        saveProgress(currentIndex, updated, false);
                        return updated;
                    });
                    setCurrentFen(game.fen());
                    setSelectedSquare(null);
                    playSound(move.captured ? 'capture' : 'move');

                    if (puzzleStep + 1 >= currentPuzzle.solution.length) {
                        setIsSuccess(true);
                        setTimeout(() => playSound('notify'), 300); 
                        setPopupText(FUN_POPUPS[Math.floor(Math.random() * FUN_POPUPS.length)]);
                    } else {
                        // 🤖 COMPUTER'S TURN
                        setPuzzleStep(prev => prev + 1);
                        setTimeout(() => {
                            const oppMove = currentPuzzle.solution[puzzleStep + 1];
                            if (oppMove) {
                                const g2 = new Chess(game.fen());
                                const m2 = g2.move({ 
                                    from: oppMove.substring(0, 2), 
                                    to: oppMove.substring(2, 4), 
                                    promotion: oppMove.length === 5 ? oppMove[4] : 'q' 
                                });
                                setCurrentFen(g2.fen());
                                playSound(m2.captured ? 'capture' : 'move');
                                setPuzzleStep(prev => prev + 1); 
                            }
                        }, 500);
                    }
                } else {
                    triggerError(from, to);
                }
            } else {
                triggerError(from, to); 
            }
        } catch (e) {
            triggerError(from, to);
        }
    };

    const handleSquareClick = (id) => {
        const pieceObj = displayBoard[id];
        if (selectedSquare) {
            executeMove(selectedSquare, id);
        } else {
            if (pieceObj && pieceObj.color === playerTurn) {
                setSelectedSquare(id);
                // 🔥 SOUND BUG FIXED: playSound('move') removed from here!
            }
        }
    };

    const handleNextPuzzle = async () => {
        const finalAttempts = [...currentAttempts];
        const logEntry = {
            puzzleNumber: currentIndex + 1,
            theme: currentPuzzle.theme || "Tactics",
            startingFen: initialFen,
            attempts: finalAttempts
        };
        const newSessionLog = [...puzzleLog, logEntry];
        setPuzzleLog(newSessionLog);

        if (currentIndex + 1 < puzzles.length) {
            const nextIndex = currentIndex + 1;
            setCurrentIndex(nextIndex);
            loadPuzzleState(puzzles[nextIndex]);
            setIsSuccess(false);
            setSelectedSquare(null);
            
            // Save progress for the next puzzle index with empty attempts
            saveProgress(nextIndex, [], false, newSessionLog);
        } else {
            setIsComplete(true);
            playSound('notify');
            fireConfetti(); 

            if (assignment?.isReplay) return;

            if (myProfile && myProfile.email) {
                const safeEmail = myProfile.email.toLowerCase();
                const savingToast = toast.loading("Saving Mission Progress...");
                
                try {
                    const userRef = doc(db, 'users', safeEmail);
                    await setDoc(userRef, { completedMissions: arrayUnion(assignment.id, assignment.levelId), totalXp: increment(50) }, { merge: true });
                } catch (err) {
                    console.error("Error updating user completedMissions:", err);
                }

                try {
                    if (assignment.assignmentDocId && assignment.assignmentDocId !== 'INDIVIDUAL') {
                        const targetDocId = assignment.assignmentDocId;
                        const docId = `${targetDocId}_${safeEmail.replace(/[^a-zA-Z0-9]/g, '')}`;
                        const cleanLogs = newSessionLog.map(log => ({
                            puzzleNumber: log.puzzleNumber || 0,
                            theme: log.theme || "Tactics",
                            startingFen: log.startingFen || "",
                            attempts: log.attempts.map(att => ({
                                san: att.san || "", from: att.from || "", to: att.to || "", isCorrect: !!att.isCorrect
                            }))
                        }));

                        await setDoc(doc(db, "homework_submissions", docId), {
                            homeworkId: targetDocId,
                            levelId: assignment.levelId || '',
                            homeworkTitle: assignment.title || 'Chess Mission',
                            batchId: assignment.batchId || 'INDIVIDUAL',
                            studentEmail: safeEmail,
                            studentName: myProfile.name || safeEmail.split('@')[0] || "Student",
                            submittedAt: new Date().toISOString(),
                            currentIndex: currentIndex,
                            totalPuzzles: puzzles.length,
                            status: 'completed',
                            logs: cleanLogs 
                        });
                        toast.success("Report Sent to Coach! 🚀", { id: savingToast });
                    } else {
                        toast.success("Mission Mastered! 🏆", { id: savingToast });
                    }
                } catch (error) {
                    console.error("Error saving final homework submission:", error);
                    toast.error("Failed to submit report to coach. Check console or firebase permissions.", { id: savingToast });
                }
            }
        }
    };

    const progressPercent = ((currentIndex) / 20) * 100;
    const isFlipped = playerTurn === 'b';
    const rows = isFlipped ? [1,2,3,4,5,6,7,8] : [8,7,6,5,4,3,2,1]; 
    const cols = isFlipped ? ['h','g','f','e','d','c','b','a'] : ['a','b','c','d','e','f','g','h'];

    return (
        <div style={{background: 'var(--bg-primary)', minHeight: '80vh', padding: '30px', borderRadius: '20px', border: '2px solid rgba(163, 177, 198, 0.4)', position: 'relative' }}>
            <style>{`
                @keyframes shake { 0% { transform: translateX(0); } 25% { transform: translateX(-8px) rotate(-1deg); } 50% { transform: translateX(8px) rotate(1deg); } 75% { transform: translateX(-8px) rotate(-1deg); } 100% { transform: translateX(0); } }
                @keyframes popIn { 0% { transform: scale(0.5); opacity: 0; } 80% { transform: scale(1.1); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
            `}</style>

            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <button 
                    onClick={onBack} 
                    style={{
                        padding: '10px 20px', 
                        background: 'var(--bg-primary)', 
                        color: 'var(--text-primary)', 
                        border: '2px solid var(--text-primary)', 
                        borderRadius: '8px', 
                        cursor: 'pointer', 
                        fontWeight: 'bold', 
                        boxShadow: 'var(--neu-shadow)' 
                    }} 
                    onMouseOver={e => {
                        e.currentTarget.style.boxShadow = 'var(--neu-shadow-hover)';
                        e.currentTarget.style.background = 'var(--btn-hover)';
                    }} 
                    onMouseOut={e => {
                        e.currentTarget.style.boxShadow = 'var(--neu-shadow)';
                        e.currentTarget.style.background = 'var(--bg-primary)';
                    }}
                >
                    ⬅ Leave Mission
                </button>
                <div style={{color: 'var(--text-secondary)', fontSize: '18px', fontWeight: 'bold' }}>{assignment.title || `Level ${assignment.levelId} • Chapter ${assignment.chapterId}`}</div>
            </div>

            <div style={{width: '100%', background: 'var(--bg-primary)', height: '15px', borderRadius: '10px', overflow: 'hidden', marginBottom: '30px', border: '1px solid rgba(163, 177, 198, 0.4)' }}>
                <div style={{ width: `${isComplete ? 100 : progressPercent}%`, height: '100%', background: 'linear-gradient(90deg, #10b981, #3b82f6)', transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }}></div>
            </div>

            {isComplete ? (
                <div style={{textAlign: 'center', padding: '50px', background: 'var(--bg-primary)', borderRadius: '20px', border: '2px solid #10b981', animation: 'popIn 0.5s ease-out' }}>
                    <div style={{fontSize: '80px', marginBottom: '20px', animation: 'bounce 2s infinite' }}>🏆</div>
                    <h2 style={{color: '#10b981', fontSize: '36px', textShadow: '0 0 10px rgba(16, 185, 129, 0.5)' }}>Mission Mastered!</h2>
                    <button onClick={onBack} style={{marginTop: '20px', padding: '15px 40px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '10px', fontSize: '20px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)' }}>Return to Dashboard</button>
                </div>
            ) : (
                <div style={{display: 'flex', gap: '40px', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'flex-start' }}>
                    
                    <div style={{width: '450px', maxWidth: '100%', pointerEvents: isSuccess ? 'none' : 'auto', animation: shakeBoard ? 'shake 0.4s ease-in-out' : 'none', border: shakeBoard ? '4px solid #ef4444' : '4px solid var(--bg-primary)', transition: 'border 0.2s', borderRadius: '12px', overflow: 'hidden', boxShadow: shakeBoard ? '0 0 30px rgba(239, 68, 68, 0.5)' : '0 10px 30px rgba(0,0,0,0.5)' }}>
                        <div style={{width: '100%', aspectRatio: '1/1', display: 'grid', gridTemplateColumns: `repeat(8, 1fr)`, gridTemplateRows: `repeat(8, 1fr)` }}>
                            {rows.map((row, rIdx) => cols.map((col, cIdx) => {
                                const id = `${col}${row}`; 
                                const isDark = (rIdx + cIdx) % 2 === 1;
                                const pieceObj = displayBoard[id];
                                const isSelected = selectedSquare === id;

                                return (
                                    <div key={id} onClick={() => handleSquareClick(id)} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); const fromSq = e.dataTransfer.getData('fromSq'); if (fromSq && fromSq !== id) executeMove(fromSq, id); }} style={{position: 'relative', background: isSelected ? '#fde047' : (isDark ? darkSquareColor : lightSquareColor), display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}>
                                        {cIdx === 0 && <span style={{position: 'absolute', top: 2, left: 3, fontSize: '12px', color: isDark ? '#eeeed2' : '#769656', fontWeight: 'bold', pointerEvents: 'none', zIndex: 5 }}>{row}</span>}
                                        {rIdx === 7 && <span style={{position: 'absolute', bottom: 1, right: 3, fontSize: '12px', color: isDark ? '#eeeed2' : '#769656', fontWeight: 'bold', pointerEvents: 'none', zIndex: 5 }}>{col}</span>}
                                        {pieceObj && <img src={currentPieceSet[pieceObj.color + pieceObj.type.toUpperCase()]} alt="piece" draggable={pieceObj.color === playerTurn} onDragStart={e => { e.dataTransfer.setData('fromSq', id); setSelectedSquare(id); }} style={{width: '90%', zIndex: 10, filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.4))' }} />}
                                    </div>
                                );
                            }))}
                        </div>
                    </div>

                    <div style={{flex: 1, minWidth: '300px', background: 'var(--bg-primary)', padding: '30px', borderRadius: '20px', border: '1px solid rgba(163, 177, 198, 0.4)' }}>
                        <div style={{background: '#3b82f6', color: 'white', display: 'inline-block', padding: '5px 15px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '15px' }}>Puzzle {currentIndex + 1} of 20</div>
                        <h2 style={{color: 'var(--text-primary)', margin: '0 0 10px 0', fontSize: '28px' }}>{currentPuzzle.theme}</h2>
                        
                        <div style={{
                            marginTop: '10px', 
                            padding: '10px 20px', 
                            background: playerTurn === 'w' ? 'var(--bg-primary)' : 'var(--text-primary)', 
                            color: playerTurn === 'w' ? 'var(--text-primary)' : 'var(--bg-primary)', 
                            borderRadius: '12px', 
                            fontWeight: 'bold', 
                            display: 'inline-block', 
                            border: '2px solid var(--text-primary)', 
                            boxShadow: 'var(--neu-shadow)' 
                        }}>
                            {puzzleStep % 2 !== 0 ? '🤖 Computer is thinking...' : (playerTurn === 'w' ? '⚪ White to Move' : '⚫ Black to Move')}
                        </div>

                        {isSuccess ? (
                            <div style={{marginTop: '30px', background: 'rgba(16, 185, 129, 0.1)', border: '2px solid #10b981', padding: '20px', borderRadius: '15px', textAlign: 'center', animation: 'popIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}>
                                <h3 style={{color: '#10b981', margin: '0 0 10px 0', fontSize: '24px' }}>{popupText}</h3>
                                <button onClick={handleNextPuzzle} style={{width: '100%', padding: '15px', background: '#10b981', color: 'black', border: 'none', borderRadius: '10px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 10px rgba(16, 185, 129, 0.4)' }}>Next Puzzle ⏩</button>
                            </div>
                        ) : (
                            <div style={{marginTop: '30px', background: shakeBoard ? 'rgba(239, 68, 68, 0.1)' : 'rgba(59, 130, 246, 0.1)', border: shakeBoard ? '1px dashed #ef4444' : '1px dashed #3b82f6', padding: '15px', borderRadius: '10px', color: shakeBoard ? '#dc2626' : '#1d4ed8', textAlign: 'center', fontSize: '15px', transition: 'all 0.2s', fontWeight: 'bold' }}>
                                {shakeBoard ? 'Oops! That is not the best move. Try again! 🚫' : `Find the best combo move to win!`}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
